// Money (F-09, NFR-12): integer minor units of an ISO 4217 currency, decimal-string rates, exact
// conversion (round half to even) and largest-remainder splits, in BigInt so nothing is a float.
// The Swift port is PFMoney; both pass shared/test-vectors/money.

const EXPONENTS: Record<string, number> = {
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
  ISK: 0,
  PYG: 0,
  UGX: 0,
  XAF: 0,
  XOF: 0,
  IDR: 2,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  JOD: 3,
  TND: 3,
  LYD: 3,
  IQD: 3,
};

export const exponent = (currency: string): number => EXPONENTS[currency.toUpperCase()] ?? 2;

/** "0.3825" → 3825n / 10_000n; null when it isn't a plain decimal. */
function decimal(s: string): { num: bigint; den: bigint } | null {
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(s.trim());
  if (!m) return null;
  const frac = m[3] ?? '';
  const num = BigInt((m[2] ?? '0') + frac) * (m[1] ? -1n : 1n);
  return { num, den: 10n ** BigInt(frac.length) };
}

/** n / d rounded half to even (d > 0). */
function roundHalfEven(n: bigint, d: bigint): bigint {
  const sign = n < 0n ? -1n : 1n;
  const a = n < 0n ? -n : n;
  const q = a / d;
  const r = a % d;
  const twice = 2n * r;
  let out = q;
  if (twice > d || (twice === d && q % 2n === 1n)) out = q + 1n;
  return out * sign;
}

/** `minor` of `from` in `to` at `rate` (1 unit of `from` = `rate` units of `to`). */
export function convert(minor: number, from: string, to: string, rate: string): number | null {
  const r = decimal(rate);
  if (!r) return null;
  const shift = exponent(to) - exponent(from);
  let num = BigInt(minor) * r.num;
  let den = r.den;
  if (shift > 0) num *= 10n ** BigInt(shift);
  else if (shift < 0) den *= 10n ** BigInt(-shift);
  return Number(roundHalfEven(num, den));
}

/** Parts summing exactly to `total` by `weights`: floors, then leftovers to the largest remainders. */
export function split(total: number, weights: string[]): number[] | null {
  const parsed = weights.map(decimal);
  const ws = parsed.filter((w): w is { num: bigint; den: bigint } => w !== null);
  if (ws.length === 0 || ws.length !== parsed.length || ws.some((w) => w.num < 0n)) return null;
  const den = ws.reduce((m, w) => (w.den > m ? w.den : m), 1n);
  const scaled = ws.map((w) => w.num * (den / w.den));
  const sum = scaled.reduce((a, b) => a + b, 0n);
  if (sum <= 0n) return null;
  const magnitude = BigInt(Math.abs(total));
  const parts = scaled.map((w) => (magnitude * w) / sum);
  const remainders = scaled.map((w) => (magnitude * w) % sum);
  let left = magnitude - parts.reduce((a, b) => a + b, 0n);
  const order = scaled
    .map((_, i) => i)
    .sort((a, b) => {
      const ra = remainders[a] ?? 0n,
        rb = remainders[b] ?? 0n;
      return ra === rb ? a - b : ra > rb ? -1 : 1;
    });
  for (const i of order) {
    if (left <= 0n) break;
    parts[i] = (parts[i] ?? 0n) + 1n;
    left -= 1n;
  }
  const sign = total < 0 ? -1 : 1;
  return parts.map((p) => Number(p) * sign);
}

/** "1,234.50" in PHP → 123450; a decimal comma with 1–2 final digits; null when invalid. */
export function parse(text: string, currency: string): number | null {
  let s = [...text].filter((c) => /[0-9.,-]/.test(c)).join('');
  if (s === '') return null;
  const commas = (s.match(/,/g) ?? []).length;
  const lastComma = s.lastIndexOf(',');
  const after = s.length - lastComma - 1;
  if (!s.includes('.') && commas === 1 && after >= 1 && after <= 2)
    s = `${s.slice(0, lastComma)}.${s.slice(lastComma + 1)}`;
  s = s.replace(/,/g, '');
  const parts = s.split('.');
  if (parts.length > 2) return null;
  const whole = parts[0] ?? '';
  const decimals = parts[1] ?? '';
  const exp = exponent(currency);
  if (decimals.length > exp || !/^\d*$/.test(decimals) || (whole === '' && parts.length < 2)) return null;
  if (!/^-?\d*$/.test(whole)) return null;
  const w = Number(whole === '' || whole === '-' ? '0' : whole);
  const frac = Number(decimals.padEnd(exp, '0') || '0');
  const scale = 10 ** exp;
  return whole.startsWith('-') ? w * scale - frac : w * scale + frac;
}

const SYMBOLS: Record<string, string> = {
  JPY: '¥',
  PHP: '₱',
  EUR: '€',
  KRW: '₩',
  USD: '$',
  GBP: '£',
  TWD: 'NT$',
  THB: '฿',
  VND: '₫',
  SGD: 'S$',
  HKD: 'HK$',
  RON: 'lei ',
};

export const symbol = (currency: string): string =>
  SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;

/** "¥1,280", "₱489.60", "-€3.00". */
export function format(minor: number, currency: string): string {
  const exp = exponent(currency);
  const scale = 10 ** exp;
  const magnitude = Math.abs(minor);
  const whole = Math.floor(magnitude / scale);
  const frac = magnitude % scale;
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const number = exp > 0 ? `${grouped}.${frac.toString().padStart(exp, '0')}` : grouped;
  return `${minor < 0 ? '-' : ''}${symbol(currency)}${number}`;
}

/** num / den as a decimal string, rounded half to even to 12 places, trailing zeros dropped. */
function ratio(num: bigint, den: bigint): string | null {
  if (den === 0n) return null;
  if (den < 0n) {
    num = -num;
    den = -den;
  }
  const scaled = roundHalfEven(num * 10n ** 12n, den);
  const neg = scaled < 0n;
  const digits = (neg ? -scaled : scaled).toString().padStart(13, '0');
  const out = `${digits.slice(0, -12)}.${digits.slice(-12)}`.replace(/\.?0+$/, '');
  return `${neg ? '-' : ''}${out}`;
}

/** 1 `base` in `quote`, from both against a common currency (EUR → base, EUR → quote), as PFStore's fxRate. */
export function crossRate(eurToBase: string, eurToQuote: string): string | null {
  const b = decimal(eurToBase);
  const q = decimal(eurToQuote);
  if (!b || !q || b.num === 0n) return null;
  return ratio(q.num * b.den, q.den * b.num);
}

/** The balance-implied rate for a settle-up: |other| ÷ |mine| in whole units (FR-BUD-16). */
export function impliedRate(
  mine: number,
  mineCurrency: string,
  other: number,
  otherCurrency: string,
): string | null {
  if (mine === 0) return null;
  const m = BigInt(Math.abs(mine)) * 10n ** BigInt(exponent(otherCurrency));
  const o = BigInt(Math.abs(other)) * 10n ** BigInt(exponent(mineCurrency));
  return ratio(o, m);
}
